import { difference, differenceBy, get, groupBy } from 'lodash'
import { Feature_Config, Feature_Type_Config, Feature_Value, Kap_Measure } from 'src/@types/graphql'
import { isFeatureSelectionInvalid } from 'src/screens/shared/feature/utils/FeatureValidationUtils'
import { CheckboxData } from 'src/shared/form/control/CheckboxGroupField'

// Form values
export interface FormValues {
  [key: string]: FeatureGroupValues
}

export interface TextFieldValue {
  id: number
  featureConfig: Feature_Config
  selected: boolean
  text: string | undefined
}

export interface FeatureGroupValues {
  name?: string
  code?: string
  feature_group_id?: string
  features: {
    checkboxes: number[]
    textFields: TextFieldValue[]
  }
}

// rendering in form
export interface TextFeatureData {
  tooltip?: string
  id: number
  featureConfig: Feature_Config
  selected: boolean
  text: string
  label?: string | undefined
}

export type FeatureItem = CheckboxData | TextFeatureData

export interface RenderFeatureGroups {
  name: string
  code: string
  features: FeatureItem[]
}

const adaptForRendering = (
  allFeatures: Feature_Config[],
  allFeatureValues: Feature_Value[],
  locale: string,
  featureValidation?: boolean,
  featureTypeConfig?: Feature_Type_Config,
  relatedEntity?: Kap_Measure,
): RenderFeatureGroups[] => {
  const groupedByGroupId = groupBy(allFeatures, (x) => x.feature_group_config?.code ?? 'DEFAULT')
  const adapted: RenderFeatureGroups[] = []

  for (const [featureCode, nestedFeatures] of Object.entries(groupedByGroupId)) {
    const checkBoxFields: FeatureItem[] = nestedFeatures
      .map((x) => {
        if (x.selection_type === 'SELECT') {
          const shouldInclude = allFeatureValues.some((feature) => feature.feature_config.id === x.id) || x.active

          if (!shouldInclude) {
            return null
          }

          return {
            label: get(x.names, locale, '') as string,
            value: x.id,
            tooltip: get(x.tooltips, locale, '') as string,
            active: x.active,
            disabled: featureValidation && isFeatureSelectionInvalid(featureTypeConfig, relatedEntity, x),
          } as CheckboxData
        } else if (x.selection_type === 'TEXT') {
          const isSelected = allFeatureValues.some((feature) => feature.feature_config.id === x.id) || x.active

          if (!isSelected) {
            return null
          }

          return {
            selected: false,
            text: '',
            id: x.id,
            featureConfig: x,
            label: get(x.names, locale, '') as string,
            tooltip: get(x.tooltips, locale, '') as string,
          } as TextFeatureData
        }
      })
      .filter((i) => i !== null && i !== undefined)
      .filter((x): x is FeatureItem => x !== null)

    // we create a section
    const section: RenderFeatureGroups = {
      name: get(nestedFeatures[0]?.feature_group_config?.names, locale, '') as string,
      code: featureCode,
      features: checkBoxFields,
    }

    adapted.push(section)
  }
  return adapted
}

const adaptInitialValues = (
  savedFeatureValues: Feature_Value[],
  featuresConfig: Feature_Config[],
  onlyAvailableInConfig: boolean,
): FormValues => {
  const groupedByGroupId = groupBy(savedFeatureValues, (x) => x.feature_config.feature_group_config?.code ?? 'DEFAULT')

  const formValues: FormValues = {}

  for (const [featureCode, nestedFeatures] of Object.entries(groupedByGroupId)) {
    let checkboxSelections = nestedFeatures
      .filter((x) => x.feature_config.selection_type === 'SELECT')
      .map((x) => x.feature_config.id)

    // for setting initial checked values in the form, cross-check in the configuration
    if (onlyAvailableInConfig) {
      checkboxSelections = checkboxSelections.filter((featureConfigId) =>
        featuresConfig.some((featureConfig) => featureConfig.id === featureConfigId),
      )
    }

    const textSelections: TextFieldValue[] = []

    nestedFeatures
      .filter((x) => x.feature_config.selection_type === 'TEXT')
      .map((textFeature) => {
        const shouldMapOther =
          !onlyAvailableInConfig ||
          featuresConfig.some((featureConfig) => featureConfig.id === textFeature.feature_config.id)

        if (!shouldMapOther) {
          return null
        }

        textSelections[textFeature.feature_config.id] = {
          id: textFeature.feature_config.id,
          featureConfig: textFeature.feature_config,
          selected: true,
          text: textFeature.other_description ?? '',
        } as TextFieldValue
      })

    const section: FeatureGroupValues = {
      features: {
        checkboxes: checkboxSelections,
        textFields: textSelections,
      },
    }

    formValues[featureCode] = section
  }

  return formValues
}

/**
 * Produces FormValues containing only the differences.
 *
 * @param currentValues current form values
 * @param initialValues initial form values
 * @param deleted should it search for deletions -> meaning the "source" will be "initialValues" and the "target" will be the "currentValues"
 * @returns {FormValues}
 */
const diffFormValues = (
  currentValues: FormValues,
  initialValues: FormValues,
  deleted: boolean,
): Partial<FormValues> => {
  const obj = Object.keys(currentValues).reduce((agg, key) => {
    agg[key] = {
      features: {
        checkboxes: [],
        textFields: [],
      },
    } as FeatureGroupValues

    if (deleted) {
      // When deleted - find items that were in initial but not in current
      agg[key].features.checkboxes = difference(
        initialValues[key]?.features?.checkboxes ?? [],
        currentValues[key]?.features?.checkboxes ?? [],
      )

      agg[key].features.textFields = differenceBy(
        (initialValues[key]?.features?.textFields ?? []).filter((i): i is TextFieldValue => i?.text !== null),
        (currentValues[key]?.features?.textFields ?? []).filter((i): i is TextFieldValue => i?.text !== null),
        'text',
      ).map((item) => ({ ...item, selected: false }))
    } else {
      // When not deleted - find items that are in current but not in initial
      agg[key].features.checkboxes = difference(
        currentValues[key]?.features?.checkboxes ?? [],
        initialValues[key]?.features?.checkboxes ?? [],
      )

      agg[key].features.textFields = differenceBy(
        (currentValues[key]?.features?.textFields ?? []).filter((i): i is TextFieldValue => i?.text !== null),
        (initialValues[key]?.features?.textFields ?? []).filter((i): i is TextFieldValue => i?.text !== null),
        'text',
      )
    }

    return agg
  }, {} as FormValues)

  return obj
}

/**
 * This function is responsible for creating Filter_Value(s) based on difference between currentValues and initialFormValues.
 *
 * @param currentValues the current values
 * @param initialValues the initial values
 * @param featureBaseId the feature base id
 * @param deleted include other feature when this value is *NOT* equal to "otherFeature.selected" value.
 *        this property is also responsible for choosing the source `features` array to be diffed.
 * @param invalidOtherFeature if there is an invalid other feature - include it to the features for deletion.
 * @returns Feature_Value[]
 */
const createFeatureValues = (
  currentValues: FormValues,
  initialValues: FormValues,
  featureBaseId: number,
  deleted: boolean,
  invalidOtherFeature?: boolean,
): Feature_Value[] => {
  const diff = diffFormValues(currentValues, initialValues || {}, deleted)

  const res = Object.values(diff)
    .map((g) => {
      const features =
        g?.features.checkboxes?.map(
          (f) =>
            ({
              feature_config_id: f,
              feature_base_id: featureBaseId,
            }) as Partial<Feature_Value>,
        ) ?? []

      g?.features.textFields?.forEach((other) => {
        if (other?.selected === !deleted || (other && invalidOtherFeature)) {
          features.push({
            feature_config_id: other.id,
            other_description: other.text,
            feature_base_id: featureBaseId,
          })
        }
      })

      return features
    })
    .flat()
    .filter(Boolean) as Feature_Value[]

  return res
}

export const FeatureBaseEditUtils = {
  adaptForRendering,
  adaptInitialValues,
  createFeatureValues,
  diffFormValues,
}
